import { PostgrestResponse } from '@supabase/supabase-js';
import { ITipoPessoa } from '../../../types/tables/ITipoPessoa';
import { BaseMultiTenantService } from '../base/BaseMultiTenantService';

/**
 * Serviço para gerenciamento de tipos de pessoa com isolamento multi-tenant
 * Gerencia níveis de acesso e permissões (Aluno, Responsável, Operador, Admin)
 */
export class TipoPessoaService extends BaseMultiTenantService<ITipoPessoa> {
     constructor() {
          super('tipo_pessoa', ['nome', 'descricao'], 'fk_id_organizacao');
     }

     /**
      * Busca tipos ativos ordenados por nível de acesso
      */
     async getAllOrdenadoPorNivel(organizationId: number): Promise<PostgrestResponse<ITipoPessoa[]>> {
          return await this.client.from(this.tableName).select('*').eq(this.organizationIdField, organizationId).is('deleted_at', null).order('nivel_acesso', { ascending: true });
     }

     /**
      * Busca tipo por nome
      */
     async getByNome(organizationId: number, nome: string): Promise<PostgrestResponse<ITipoPessoa[]>> {
          return await this.client.from(this.tableName).select('*').eq(this.organizationIdField, organizationId).eq('nome', nome).is('deleted_at', null).single();
     }

     /**
      * Busca tipos com determinado nível de acesso ou superior
      */
     async getByNivelAcessoMinimo(organizationId: number, nivelMinimo: number): Promise<PostgrestResponse<ITipoPessoa[]>> {
          return await this.client.from(this.tableName).select('*').eq(this.organizationIdField, organizationId).gte('nivel_acesso', nivelMinimo).is('deleted_at', null).order('nivel_acesso', { ascending: true });
     }

     /**
      * Verifica se um tipo de pessoa pode realizar determinada ação
      */
     async verificarPermissao(organizationId: number, tipoId: number, permissao: string): Promise<boolean> {
          const { data: tipo } = await this.getById(tipoId, organizationId);

          if (!tipo) {
               return false;
          }

          return Boolean((tipo as any)[permissao]);
     }

     /**
      * Cria tipos padrão para uma nova organização
      */
     async criarTiposPadrao(organizationId: number, userId?: number): Promise<any[]> {
          const tiposPadrao = [
               {
                    nome: 'Aluno',
                    descricao: 'Estudante da instituição',
                    nivel_acesso: 1,
                    pode_cadastrar_itens: false,
                    pode_editar_itens: false,
                    pode_excluir_itens: false,
                    pode_visualizar_financeiro: false,
                    pode_gerenciar_usuarios: false,
                    pode_acessar_relatorios: false,
                    pode_configurar_sistema: false,
                    pode_gerenciar_integracao: false,
                    pode_gerenciar_tipos_pessoa: false,
                    pode_acessar_logs: false,
               },
               {
                    nome: 'Responsável',
                    descricao: 'Pais ou tutores dos alunos',
                    nivel_acesso: 2,
                    pode_cadastrar_itens: false,
                    pode_editar_itens: false,
                    pode_excluir_itens: false,
                    pode_visualizar_financeiro: true,
                    pode_gerenciar_usuarios: false,
                    pode_acessar_relatorios: false,
                    pode_configurar_sistema: false,
                    pode_gerenciar_integracao: false,
                    pode_gerenciar_tipos_pessoa: false,
                    pode_acessar_logs: false,
               },
               {
                    nome: 'Operador',
                    descricao: 'Funcionário da cantina',
                    nivel_acesso: 5,
                    pode_cadastrar_itens: true,
                    pode_editar_itens: true,
                    pode_excluir_itens: false,
                    pode_visualizar_financeiro: true,
                    pode_gerenciar_usuarios: false,
                    pode_acessar_relatorios: true,
                    pode_configurar_sistema: false,
                    pode_gerenciar_integracao: false,
                    pode_gerenciar_tipos_pessoa: false,
                    pode_acessar_logs: false,
               },
               {
                    nome: 'Coordenador',
                    descricao: 'Coordenador da cantina',
                    nivel_acesso: 8,
                    pode_cadastrar_itens: true,
                    pode_editar_itens: true,
                    pode_excluir_itens: true,
                    pode_visualizar_financeiro: true,
                    pode_gerenciar_usuarios: true,
                    pode_acessar_relatorios: true,
                    pode_configurar_sistema: true,
                    pode_gerenciar_integracao: false,
                    pode_gerenciar_tipos_pessoa: true,
                    pode_acessar_logs: true,
               },
               {
                    nome: 'Administrador',
                    descricao: 'Administrador do sistema',
                    nivel_acesso: 10,
                    pode_cadastrar_itens: true,
                    pode_editar_itens: true,
                    pode_excluir_itens: true,
                    pode_visualizar_financeiro: true,
                    pode_gerenciar_usuarios: true,
                    pode_acessar_relatorios: true,
                    pode_configurar_sistema: true,
                    pode_gerenciar_integracao: true,
                    pode_gerenciar_tipos_pessoa: true,
                    pode_acessar_logs: true,
               },
          ];

          const resultados = [];

          for (const tipo of tiposPadrao) {
               try {
                    const resultado = await this.create(tipo, organizationId, userId);
                    resultados.push(resultado);
               } catch (error) {
                    console.error(`Erro ao criar tipo ${tipo.nome}:`, error);
               }
          }

          return resultados;
     }

     /**
      * Verifica se o tipo pode ser excluído
      */
     async canDelete(id: number, organizationId: number): Promise<{ canDelete: boolean; reason?: string }> {
          // Verificar se há pessoas usando este tipo
          const { data: pessoas } = await this.client.from('pessoas').select('id').eq('fk_id_tipo_pessoa', id).eq('fk_id_organizacao', organizationId).is('deleted_at', null);

          if (pessoas && pessoas.length > 0) {
               return { canDelete: false, reason: 'Existem pessoas vinculadas a este tipo' };
          }

          return { canDelete: true };
     }

     /**
      * Atualiza permissões de um tipo
      */
     async updatePermissoes(id: number, organizationId: number, permissoes: Partial<ITipoPessoa>, userId?: number): Promise<PostgrestResponse<ITipoPessoa[]>> {
          // Filtrar apenas campos de permissão
          const camposPermissao = ['pode_cadastrar_itens', 'pode_editar_itens', 'pode_excluir_itens', 'pode_visualizar_financeiro', 'pode_gerenciar_usuarios', 'pode_acessar_relatorios', 'pode_configurar_sistema', 'pode_gerenciar_integracao', 'pode_gerenciar_tipos_pessoa', 'pode_acessar_logs'];

          const permissoesLimpas = Object.keys(permissoes)
               .filter((key) => camposPermissao.includes(key))
               .reduce((obj, key) => {
                    obj[key] = permissoes[key as keyof ITipoPessoa];
                    return obj;
               }, {} as any);

          return await this.update(id, permissoesLimpas, organizationId, userId);
     }
}

export const tipoPessoaService = new TipoPessoaService();
